// scripts/modelist/light.mjs
// What the runner (modelist-host.mjs, views.mjs) and the app (lib/modelist/light.ts) must agree on about a saved light
// and a saved place: their bounds and the one strict read of either, since what is stored is read back by any browser
// and by the Modelist's own tools. One declaration for both: under scripts/modelist/ because the runner's image holds
// nothing else of the repository (scripts/modelist/Dockerfile).

/** The numbers of a light and their bounds. The elevation goes under the horizon, through the whole twilight; the day's last quarter hour is 23:45. */
export const LIGHT_RANGES = Object.freeze({
  azimuth: Object.freeze({ min: 0, max: 360, step: 1 }),
  elevation: Object.freeze({ min: -10, max: 90, step: 1 }),
  time: Object.freeze({ min: 0, max: 23.75, step: 0.25 }),
});
export const LIGHT_BACKDROPS = Object.freeze(['studio', 'sky']);
export const LIGHT_SUNS = Object.freeze(['angles', 'time']);

const DATE = /^(\d{4})-(\d\d)-(\d\d)$/;
/**
 * Whether a text is a date a light takes: YYYY-MM-DD, a day that exists, of a year the solar formula and Date both
 * hold. A date field typed digit by digit emits years 2, 20 and 202 on the way to 2027, which Date reads as 1902,
 * 1920 and 0202; and Date rolls 2026-13-45 over into 2027 instead of refusing it.
 */
export const validDate = (date) => {
  const [, year, month, day] = (typeof date === 'string' && DATE.exec(date)) || [];
  if (!(Number(year) >= 1900 && Number(year) <= 2200)) return false;
  const at = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return at.getUTCMonth() === Number(month) - 1 && at.getUTCDate() === Number(day);
};

/**
 * The time zone a typed text names, as this engine spells it ("europe/paris" is "Europe/Paris"), or null: what a zone
 * typed by the user is stored as. An offset ("+07:00"), which some engines take for a zone, is none: the tz database
 * the other readers use (Python's zoneinfo) has no such name.
 */
export const zoneName = (typed) => {
  if (typeof typed !== 'string' || !typed || typed.length > 64) return null;
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: typed }).resolvedOptions().timeZone;
    return /^[+-]/.test(name) ? null : name;
  } catch { return null; }
};
const ZONE_SHAPE = /^[A-Z][A-Za-z0-9_+-]*(\/[A-Z0-9][A-Za-z0-9_+-]*)*$/;
/**
 * Whether a stored text is a time zone's name in the tz database ("Europe/Paris", "Etc/GMT-7", "UTC"), spelled as the
 * database spells it. Engines disagree on which of two names of one zone is the canonical one ("Asia/Kolkata" is
 * "Asia/Calcutta" to V8), so a name is taken when it is this engine's own or another name of the same zone, and
 * refused when it is the same name in other letters' case, which a case-sensitive reader would not find.
 */
export const validZone = (zone) => {
  const name = zoneName(zone);
  return name !== null && ZONE_SHAPE.test(zone) && (name === zone || name.toLowerCase() !== zone.toLowerCase());
};

const within = (value, bound) => typeof value === 'number' && Math.abs(value) <= bound;
/**
 * A place as it is stored, or null. Its coordinates and its time zone, which a stored place always carries, are taken
 * as they are or the place is none. Its north alone is normalised: within one turn, and up when it names none, as a
 * calibration without one is. `zoned: false` reads a place that has no zone yet (a site's calibration), and leaves
 * any it names out.
 */
export const cleanPlace = (place, { zoned = true } = {}) => {
  if (!within(place?.lat, 90) || !within(place.lon, 180) || (zoned && !validZone(place.zone))) return null;
  const north = typeof place.north === 'number' && Number.isFinite(place.north) ? place.north : 0;
  return { lat: place.lat, lon: place.lon, north: ((north % 360) + 360) % 360, ...(zoned ? { zone: place.zone } : {}) };
};

const toTheMetre = (degrees) => (typeof degrees === 'number' ? Math.round(degrees * 1e5) / 1e5 : degrees);
/**
 * A site's place from its calib.json (site3d/grab.py writes geo.lat, geo.lon and geo.north_deg), to the metre (five
 * decimals) and without a zone; null when it holds none.
 */
export const calibPlace = (calib) => cleanPlace({ lat: toTheMetre(calib?.geo?.lat), lon: toTheMetre(calib?.geo?.lon), north: calib?.geo?.north_deg }, { zoned: false });

// Where a sun stands (sunAt): an azimuth within one turn, an elevation from the nadir to the zenith.
const sunPlace = (at) => (typeof at?.azimuth === 'number' && at.azimuth >= 0 && at.azimuth <= 360 && typeof at.elevation === 'number' && Math.abs(at.elevation) <= 90
  ? { azimuth: at.azimuth, elevation: at.elevation } : null);
/**
 * A light as it is stored, or null: every field of it present and in its bounds, nothing clamped. By date and time it
 * carries the place it stands over, zone included, and where its sun then stands (sunAt), worked out by the app's
 * server: this module has no astronomy, and takes that position as two angles or refuses the light. One stored
 * before sunAt existed is still a light, which a reader without astronomy cannot apply. By its angles a light
 * carries no place, and its sunAt is those angles, whatever it was handed.
 */
export const cleanLight = (light) => {
  if (!LIGHT_BACKDROPS.includes(light?.backdrop) || !LIGHT_SUNS.includes(light.sun) || !validDate(light.date)) return null;
  for (const [key, { min, max }] of Object.entries(LIGHT_RANGES)) if (!(typeof light[key] === 'number' && light[key] >= min && light[key] <= max)) return null;
  const place = light.sun === 'time' ? cleanPlace(light.place) : null;
  if (light.sun === 'time' && !place) return null;
  const sunAt = light.sun === 'angles' ? { azimuth: light.azimuth, elevation: light.elevation } : light.sunAt === undefined ? undefined : sunPlace(light.sunAt);
  if (sunAt === null) return null;
  return { backdrop: light.backdrop, sun: light.sun, azimuth: light.azimuth, elevation: light.elevation, date: light.date, time: light.time, ...(place ? { place } : {}), ...(sunAt ? { sunAt } : {}) };
};

/**
 * What of a light decides the picture, in one order of keys: its backdrop and the pair that places its sun, with,
 * under a sun by date and time, the place and where the sun was found to stand (by its angles the sun stands at
 * them). The other pair is kept in the light and draws nothing: two lights that differ by it alone give the same
 * picture.
 */
export const lightInForce = (light) => (light.sun === 'time'
  ? { backdrop: light.backdrop, sun: light.sun, date: light.date, time: light.time, place: light.place && { lat: light.place.lat, lon: light.place.lon, north: light.place.north, zone: light.place.zone },
    ...(light.sunAt ? { sunAt: { azimuth: light.sunAt.azimuth, elevation: light.sunAt.elevation } } : {}) }
  : { backdrop: light.backdrop, sun: light.sun, azimuth: light.azimuth, elevation: light.elevation });
/** The light a picture was rendered under, as it is compared: none is the empty text. */
export const lightPrint = (light) => (light && typeof light === 'object' ? JSON.stringify(lightInForce(light)) : '');

/** What whoever refuses a save of the project's light or place tells the user: the app's server first, then the runner. */
export const LIGHT_REFUSED = 'This light cannot be saved: check its settings.';
/**
 * A save of the project's default light, of its place, or of both, as it is asked: each whole or the save is none
 * (null), and at least one of them.
 */
export const cleanLightSave = (asked) => {
  const light = asked?.light === undefined ? undefined : cleanLight(asked.light);
  const place = asked?.place === undefined ? undefined : cleanPlace(asked.place);
  return light === null || place === null || (!light && !place) ? null : { ...(light ? { light } : {}), ...(place ? { place } : {}) };
};

/**
 * A project's light and place as a `views` answer carries them, read again by whoever receives it: the default light
 * whole or not at all, and the place with its zone when it is a saved one, without when it is the calibration's.
 */
export const readLight = (answer) => {
  const light = cleanLight(answer?.light);
  const place = cleanPlace(answer?.place) ?? cleanPlace(answer?.place, { zoned: false });
  return { ...(light ? { light } : {}), ...(place ? { place } : {}) };
};

/**
 * What a project says of its light, from its views.json and its calib.json as read (either may be missing or
 * anything): its default light, and its place, the one saved in views.json before the calibration's own.
 */
export const projectLight = (saved, calib) => readLight({ light: saved?.light, place: cleanPlace(saved?.place) ?? calibPlace(calib) });
